import React from 'react';
import bannerImage from '../../assets/banners/BANNER_SOBRE.jpg';
import { Box, Typography, useTheme } from '@mui/material';
import { FlagRounded, TravelExploreRounded, WorkspacePremiumRounded } from '@mui/icons-material';

const Sobre: React.FC = () => {
  const theme = useTheme();

  // Cards criados dentro do componente para usar o theme
  const cards = [
    {
      title: 'Missão',
      text: 'Educar para o trabalho, de forma inovadora e inclusiva, em atividades do comércio de bens, serviços e turismo.',
      icon: (
        <FlagRounded
          sx={{
            fontSize: { xs: 40, sm: 48, md: 56 },
            color: theme.palette.warning.main,
            textShadow: '2px 2px 6px rgba(0,0,0,0.4)',
          }}
        />
      ),
    },
    {
      title: 'Visão',
      text: 'Transformar vidas e fortalecer o setor de comércio de bens, serviços e turismo.',
      icon: (
        <TravelExploreRounded
          sx={{
            fontSize: { xs: 40, sm: 48, md: 56 },
            color: theme.palette.warning.main,
            textShadow: '2px 2px 6px rgba(0,0,0,0.4)',
          }}
        />
      ),
    },
    {
      title: 'Valores',
      text:
        'Ética e transparência, diversidade, inovação, sustentabilidade e transformação social.',
      icon: (
        <WorkspacePremiumRounded
          sx={{
            fontSize: { xs: 40, sm: 48, md: 56 },
            color: theme.palette.warning.main,
            textShadow: '2px 2px 6px rgba(0,0,0,0.4)',
          }}
        />
      ),
    },
  ];

  return (
    <>
      {/* Seção full-bleed (banner ocupa 100% da viewport) */}
      <Box sx={{ pt: { xs: 5, md: 8 } }}>
        <Box
          component="img"
          src={bannerImage}
          alt="Imagem representativa"
          sx={{
            display: 'block',
            ml: 'calc(50% - 50vw)',
            mr: 'calc(50% - 50vw)',
            borderRadius: 0,
            // Desktop fixo 1920x300; Mobile/Tablet mostra a imagem inteira sem corte
            width: { xs: '100vw', sm: '100vw', md: '1920px' },
            height: { xs: 'auto', sm: 'auto', md: '300px' },
            maxWidth: { xs: '100%', sm: '100%', md: 'none' },
            objectFit: { xs: 'contain', sm: 'contain', md: 'cover' },
            objectPosition: 'center',
          }}
        />

        {/* Bloco de conteúdo sem Container, apenas com padding lateral */}
        <Box sx={{ px: { xs: 2, sm: 3, md: 6 }, mt: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
  <Typography
    variant="h4"
    component="h1"
    sx={{
      textTransform: 'uppercase',
      fontWeight: 700,
      letterSpacing: { xs: 0.5, md: 0.2 },
      position: 'relative',
      mb: { xs: 2, md: 3 },
      color: theme.palette.text.primary,
      fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2rem' },
      textAlign: { xs: 'center', md: 'left' }, // <-- alinhamento responsivo
      '&::after': {
        content: '""',
        position: 'absolute',
        left: { xs: '50%', md: 0 },  // centraliza no mobile, esquerda no desktop
        bottom: 0,
        transform: { xs: 'translateX(-50%)', md: 'none' },
        width: { xs: 56, sm: 72, md: 80 },
        height: 4,
        backgroundColor: theme.palette.secondary.main,
        borderRadius: theme.shape.borderRadius,
      },
    }}
  >
    Faculdade de Tecnologia Senac Amazonas
  </Typography>

  {[...conteudo].map((texto, i) => (
    <Typography
      key={i}
      variant="body1"
      sx={{
        mb: { xs: 2, md: 2.5 },
        textAlign: { xs: 'justify', md: 'left' }, // <-- parágrafos à esquerda no desktop
        fontSize: { xs: '0.98rem', sm: '1.02rem', md: '1.05rem' },
        textIndent: { xs: '1em', sm: '1.5em', md: '0' }, // no desktop remove indentação
        lineHeight: { xs: 1.7, md: 1.8 },
        '& a': {
          color: theme.palette.secondary.main,
          fontWeight: 600,
          textDecoration: 'underline',
          wordBreak: 'break-word',
        },
      }}
      dangerouslySetInnerHTML={{ __html: texto }}
    />
  ))}
</Box>
      </Box>

      {/* Cards em full-width com gradiente alinhado ao tema */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: { xs: 2, sm: 3, md: 4 },
          mt: { xs: 4, md: 6 },
          px: { xs: 2, sm: 3, md: 6 },
          pb: { xs: 6, md: 10 },
          maxWidth: 1400,
          mx: 'auto',
        }}
      >
        {cards.map((card, i) => (
          <Box
            key={i}
            sx={{
              flex: {
                xs: '1 1 100%',   // 1 por linha no mobile
                sm: '1 1 48%',    // 2 por linha em tablets
                md: '1 1 300px',  // 3 colunas quando couber
              },
              maxWidth: { xs: '100%', sm: '48%', md: 340 },
              display: 'flex',
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                px: { xs: 2.5, md: 3 },
                py: { xs: 3, md: 4 },
                background: 'linear-gradient(135deg, #1d2671, #3a0ca3)',
                borderRadius: { xs: 1.5, md: 2 },
                boxShadow: '0 4px 12px rgba(3, 1, 133, 0.35)',
                textAlign: 'center',
                minHeight: { xs: 280, sm: 320, md: 360 },
                color: '#fff',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: { xs: 'none', md: 'translateY(-6px)' },
                  boxShadow: { xs: '0 4px 12px rgba(1, 3, 107, 0.35)', md: '0 6px 24px rgba(1, 3, 107, 0.5)' },
                },
              }}
            >
              <Box sx={{ mb: { xs: 1.5, md: 2 } }}>{card.icon}</Box>
              <Typography
                variant="h6"
                sx={{
                  mb: 1,
                  fontWeight: 700,
                  fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' },
                  color: '#fff',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: '50%',
                    bottom: -6,
                    transform: 'translateX(-50%)',
                    width: { xs: 32, sm: 36, md: 40 },
                    height: 3,
                    backgroundColor: theme.palette.secondary.main,
                    borderRadius: theme.shape.borderRadius,
                  },
                }}
              >
                {card.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: { xs: 1, md: 1.5 },
                  fontSize: { xs: '0.98rem', md: '1rem' },
                  lineHeight: { xs: 1.55, md: 1.6 },
                  color: '#F8FAFC',
                }}
              >
                {card.text}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </>
  );
};

const conteudo = [
  'A Faculdade de Tecnologia Senac Amazonas tem como missão educar para o trabalho de forma inovadora e inclusiva, voltada ao comércio de bens, serviços e turismo. Credenciada pelo MEC em 2016, iniciou suas atividades em 2017 com o curso de Gastronomia e, desde então, ampliou sua atuação com novos cursos superiores de tecnologia, alinhados às demandas do setor produtivo regional.',
  'Seus cursos são estruturados para desenvolver competências práticas, com foco na aplicação dos conhecimentos e uso de metodologias ativas. A infraestrutura conta com laboratórios equipados e um corpo docente formado por especialistas, mestres e doutores com sólida experiência acadêmica e profissional.',
  'A Faculdade também integra a Rede Nacional de Educação a Distância, oferecendo desde 2012 cursos EAD de graduação, pós-graduação e extensão, com suporte tecnológico robusto, biblioteca física e virtual, e parcerias estratégicas que conectam teoria e prática.',
  'O Programa Verticaliza Senac complementa esse compromisso, integrando o ensino técnico ao superior por meio de itinerários formativos que valorizam os saberes já adquiridos. Dessa forma, a Faculdade reafirma sua vocação para a formação continuada e sua relevância no cenário da educação profissional.',
];

export default Sobre;
